import api from '../../api'
import TextInput from '../../ui/form/TextInput'
import { ExternalIdRule, Project } from '../../types'
import FormWrapper from '../../ui/form/FormWrapper'
import { SingleSelect } from '../../ui/form/SingleSelect'
import SwitchField from '../../ui/form/SwitchField'
import Heading from '../../ui/Heading'
import { LocaleTextField } from '../settings/Locales'
import { useTranslation } from 'react-i18next'
import { useSearchTableState } from '../../components'
import { useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Tabs } from '../../ui'
import { Column, Columns } from '../../ui/Columns'
import ImageField from '../../ui/form/ImageField'

// eslint-disable-next-line @typescript-eslint/no-namespace
export declare namespace Intl {
    type Key = 'calendar' | 'collation' | 'currency' | 'numberingSystem' | 'timeZone' | 'unit'
    function supportedValuesOf(input: Key): string[]

    interface DateTimeFormat {
        // eslint-disable-next-line @typescript-eslint/method-signature-style
        format(date?: Date | number): string
        // eslint-disable-next-line @typescript-eslint/method-signature-style
        resolvedOptions(): ResolvedDateTimeFormatOptions
    }

    interface ResolvedDateTimeFormatOptions {
        locale: string
        timeZone: string
        timeZoneName?: string
    }

    // eslint-disable-next-line no-var
    var DateTimeFormat: {
        new(locales?: string | string[]): DateTimeFormat
        (locales?: string | string[]): DateTimeFormat
    }
}

interface ProjectFormProps {
    project?: Project
    localesVersion?: number
    onSave?: (project: Project) => void
}

interface ProjectMessageSettingsProps {
    projectId: number
    localesVersion?: number
    form: UseFormReturn<Project>
}

const ProjectMessageSettings = ({ projectId, localesVersion, form }: ProjectMessageSettingsProps) => {
    const { t } = useTranslation()

    const state = useSearchTableState(useCallback(async params => await api.locales.search(projectId, params), [projectId, localesVersion]))

    const tabs = state.results?.results.map(locale => ({
        key: locale.key,
        label: locale.label,
        children: <div style={{ width: '100%' }}>
            <TextInput.Field
                form={form}
                key={locale.key}
                name={`text_opt_out.first.${locale.key}`}
                prefix={locale.label}
                label={t('project_sms_opt_out_first_message')}
                subtitle={t('project_sms_opt_out_first_message_subtitle')} />
            <TextInput.Field
                form={form}
                key={locale.key}
                name={`text_opt_out.regular.${locale.key}`}
                prefix={locale.label}
                label={t('project_sms_opt_out_regular_message')}
                subtitle={t('project_sms_opt_out_regular_message_subtitle')} />
            <TextInput.Field
                form={form}
                name={`text_help.${locale.key}`}
                prefix={locale.label}
                label={t('project_sms_help_message')}
                subtitle={t('project_sms_help_message_subtitle')} />
        </div>,
    })) ?? []

    return (
        <Tabs tabs={tabs} />
    )
}

export default function ProjectForm({ project, localesVersion, onSave }: ProjectFormProps) {
    const { t } = useTranslation()
    const timeZones = Intl.supportedValuesOf('timeZone')
    const locale = navigator.languages[0]?.split('-')[0] ?? 'en'
    const defaults = project ?? {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        locale,
    }

    return (
        <FormWrapper<Project>
            defaultValues={defaults}
            onSubmit={async ({ id, name, description, locale, timezone, text_opt_out, text_help, link_wrap_email, link_wrap_push, link_wrap_in_app, locale_override, logo_url, external_id_rule, allow_email_identification }) => {

                // Clean up external_id_rule: remove if no type selected, strip empty optional fields
                let cleanedRule: ExternalIdRule | undefined
                if (external_id_rule?.type) {
                    cleanedRule = { type: external_id_rule.type }
                    if (external_id_rule.pattern) cleanedRule.pattern = external_id_rule.pattern
                    if (external_id_rule.example) cleanedRule.example = external_id_rule.example
                }

                const params = { name, description, locale, timezone, text_opt_out, text_help, link_wrap_email, link_wrap_push, link_wrap_in_app, locale_override, logo_url, external_id_rule: cleanedRule, allow_email_identification }

                const project = id
                    ? await api.projects.update(id, params)
                    : await api.projects.create(params)
                onSave?.(project)
            }}
            submitLabel={project ? t('save_settings') : t('create_project')}
        >
            {
                form => (
                    <>
                        <TextInput.Field form={form} name="name" label={t('name')} required />
                        <TextInput.Field form={form} name="description" label={t('description')} textarea />
                        <ImageField form={form} name="logo_url" label={t('logo')} />
                        <Heading size="h4" title={t('defaults')} />
                        <LocaleTextField
                            form={form}
                            name="locale"
                            label={t('default_locale')}
                            subtitle={t('default_locale_description')}
                            required />
                        <SingleSelect.Field
                            form={form}
                            options={timeZones}
                            name="timezone"
                            label={t('timezone')}
                            required
                        />
                        <Heading size="h4" title={t('message_settings')} />

                        {project?.id && <ProjectMessageSettings projectId={project.id} localesVersion={localesVersion} form={form} />}

                        <SwitchField
                            form={form}
                            name="link_wrap_email"
                            label={t('project_link_wrapping_email')}
                            subtitle={t('project_link_wrapping_email_subtitle')} />
                        <SwitchField
                            form={form}
                            name="link_wrap_push"
                            label={t('project_link_wrapping_push')}
                            subtitle={t('project_link_wrapping_push_subtitle')} />
                        <SwitchField
                            form={form}
                            name="link_wrap_in_app"
                            label={t('project_link_wrapping_in_app')}
                            subtitle={t('project_link_wrapping_in_app_subtitle')} />
                        <Heading size="h4" title={t('user_identification')} />
                        <SwitchField
                            form={form}
                            name="allow_email_identification"
                            label={t('allow_email_identification')}
                            subtitle={t('allow_email_identification_subtitle')} />
                        <Heading size="h4" title={t('external_id_validation')}>
                            {t('external_id_validation_description')}
                        </Heading>
                        <Columns>
                            <Column>
                                <SingleSelect.Field
                                    form={form}
                                    options={[
                                        { key: '', label: t('no_validation') },
                                        { key: 'string', label: t('external_id_type_string') },
                                        { key: 'number', label: t('external_id_type_number') },
                                        { key: 'uuid', label: t('external_id_type_uuid') },
                                        { key: 'email', label: t('external_id_type_email') },
                                    ]}
                                    name="external_id_rule.type"
                                    label={t('external_id_type')}
                                    subtitle={t('external_id_type_subtitle')}
                                    toValue={o => o.key}
                                    getValueKey={v => v as string}
                                    getOptionDisplay={o => o.label}
                                />
                            </Column>
                            <Column>
                                <TextInput.Field
                                    form={form}
                                    name="external_id_rule.pattern"
                                    label={t('external_id_pattern')}
                                    subtitle={t('external_id_pattern_subtitle')} />
                            </Column>
                            <Column>
                                <TextInput.Field
                                    form={form}
                                    name="external_id_rule.example"
                                    label={t('external_id_example')}
                                    subtitle={t('external_id_example_subtitle')} />
                            </Column>
                        </Columns>
                        <Heading size="h4" title={t('project_overrides')} />
                        <TextInput.Field
                            form={form}
                            name="locale_override"
                            label={t('project_locale_override')}
                            subtitle={t('project_locale_override_subtitle')} />
                    </>
                )
            }
        </FormWrapper>
    )
}
